import { AxiosInstance, AxiosResponse } from "axios";
import { ContractNoteRequest } from "../types/Transactions";
import { downloadFile, getFilenameFromHeaders, getResponseFilename } from "./api";
import { Language } from "../i18n/languages";
import { OwnershipGraphType } from "../stores/ShareholdersStore";
import { DCEvent, logErrorEvent, showGeneralError, thunk } from "../events/eventsDomain";
import { SelectedPortfolioOption } from "../portfolio/portfolioDomain";
import { ExportFormat } from "../shareholderRegister/shareholders/ExportShareholdersDomain";

export class DownloadService {
  constructor(private api: AxiosInstance) {}

  exportShareholders(
    orgNumber: string,
    language: string,
    format: string,
    date: string,
    exportFormat: ExportFormat,
  ): Promise<AxiosResponse> {
    return this.api.get(`/shareholder/export/${orgNumber}`, {
      params: {
        language,
        format,
        date,
        exportFormat,
      },
      responseType: "blob",
    });
  }

  exportShareholdersBatch(
    organizationnumbers: string[],
    language: string,
    format: string,
    date: string,
    exportFormat: ExportFormat,
  ): Promise<AxiosResponse> {
    return this.api.post(
      `/shareholder/exports`,
      {
        organizationnumbers,
        language,
        format,
        date,
        exportFormat,
      },
      {
        responseType: "blob",
      },
    );
  }

  exportShareholdersData(orgNumber: string): Promise<AxiosResponse> {
    return this.api.get(`/shareholder/export-shareholders/${orgNumber}`, {
      responseType: "blob",
    });
  }

  downloadContractNote(transactionLogId: number): Promise<void> {
    return this.api
      .get(`/contract-note-pdf/${transactionLogId}`, {
        headers: { Accept: "*/*" },
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getFilenameFromHeaders(response.headers));
      });
  }

  downloadContractNoteForCurrentUser(
    organisationNumber: string,
    request: ContractNoteRequest,
    shareholderId: number,
  ): Promise<void> {
    return this.api
      .post(
        `/shareholder-view/${organisationNumber}/download-contract-note/${shareholderId}`,
        request,
        {
          headers: { Accept: "application/pdf" },
          responseType: "blob",
        },
      )
      .then((response: AxiosResponse) => {
        downloadFile(response.data, request.fileName);
      });
  }

  downloadShareholderCertificate(
    shareholderId: string | number,
    locale: Language,
  ): Promise<AxiosResponse> {
    return this.api.get(`/shareholder-certificate/${shareholderId}?lang=${locale}`, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    });
  }

  downloadTrankMap(organisationNumber: string, threshold: number): Promise<string> {
    return this.api
      .get<string>(`/company/shareholder-map/${organisationNumber}/${threshold}`, {
        headers: { Accept: "image/svg+xml" },
      })
      .then((r) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(r.data)))}`);
  }

  downloadTrankMapAsPDF(organisationNumber: string, threshold: number) {
    return this.api
      .get<string>(`/company/shareholder-map-pdf/${organisationNumber}/${threshold}`, {
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  downloadTrankSubsidiariesMap({
    organisationNumber,
    threshold,
    maxnum,
    ownershipGraphType,
  }: {
    organisationNumber: string;
    threshold: number;
    maxnum: number;
    ownershipGraphType: OwnershipGraphType;
  }): Promise<string> {
    return this.api
      .get<string>(
        `/company/subsidiary-map/${organisationNumber}/${ownershipGraphType}/${threshold}/${maxnum}`,
        {
          headers: { Accept: "image/svg+xml" },
        },
      )
      .then((r) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(r.data)))}`);
  }

  downloadTrankSubsidiariesMapAsPDF(
    organisationNumber: string,
    threshold: number,
    maxnum: number,
    ownershipGraphType: OwnershipGraphType,
  ) {
    return this.api
      .get<string>(
        `/company/subsidiary-map-pdf/${organisationNumber}/${ownershipGraphType}/${threshold}/${maxnum}`,
        {
          responseType: "blob",
        },
      )
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  downloadTrankSubsidiariesAsExcel(organisationNumber: string, threshold: number, maxnum: number) {
    return this.api
      .get<string>(`/company/subsidiary-excel/${organisationNumber}/${threshold}/${maxnum}`, {
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  downloadShareholderCertificateForCurrentUser(
    organisationNumber: string,
    shareholderId: number,
    locale: Language,
  ): Promise<void> {
    return this.api
      .get(`/shareholder-view/${organisationNumber}/certificate/${shareholderId}/${locale}`, {
        headers: { Accept: "application/pdf" },
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  downloadNoticeToGeneralMeetingAttachment(noticeOfGeneralMeetingEmailId: string, fileId: string) {
    return downloadFileContent(
      `/shareholder-view/notice-general-meeting/${noticeOfGeneralMeetingEmailId}/attachment/${fileId}`,
      this.api,
    );
  }

  exportPortfolioShareholdersExcelFile(selectedPortfolio: SelectedPortfolioOption) {
    const url =
      selectedPortfolio.type === "portfolio-instance"
        ? `/portfolio/${selectedPortfolio.portfolio.id}/export-shareholders`
        : `/portfolio/export-shareholders`;
    return this.api.get(url, {
      responseType: "blob",
    });
  }
}

export function downloadPdf(url: string, api: AxiosInstance) {
  return api
    .get(url, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) => {
      downloadFile(response.data, getFilenameFromHeaders(response.headers));
    });
}

export function downloadFileContent(url: string, api: AxiosInstance) {
  return api
    .post(url, null, {
      headers: { Accept: "*/*" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) =>
      downloadFile(response.data, getFilenameFromHeaders(response.headers)),
    )
    .catch(() => {
      return Promise.reject("failed to download the attachment:" + url);
    });
}

export function downloadDocument(url: string, api: AxiosInstance, filename?: string) {
  return api
    .get(url, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) =>
      downloadFile(
        response.data,
        filename ?? getFilenameFromHeaders(response.headers) ?? "file.pdf",
      ),
    );
}

export type DownloadResult = [
  file: Blob | { data: Blob | ArrayBuffer },
  filename: string | undefined,
];

export function download(url: string, api: AxiosInstance): Promise<DownloadResult> {
  return api
    .get(url, {
      headers: { Accept: "application/pdf, application/zip" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) => [response.data, getFilenameFromHeaders(response.headers)]);
}

export const downloadEvent = ({
  url,
  onSuccess,
  onFailure,
}: {
  url: string;
  onSuccess?: () => DCEvent[];
  onFailure?: () => DCEvent[];
}): DCEvent[] => [
  {
    type: "http",
    method: "get",
    url,
    headers: { Accept: "application/pdf, application/zip" },
    responseType: "blob",
    onSuccess: (response) => [
      thunk(() => {
        downloadFile(response.data, getFilenameFromHeaders(response.headers));
      }),
      ...(onSuccess?.() ?? []),
    ],
    onFailure: (err) => [
      logErrorEvent(err, `Failed to download file: ${url}`),
      showGeneralError(),
      ...(onFailure?.() ?? []),
    ],
  },
];
